import { Badge, Group, Paper, Stack, Text } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import dayjs from "dayjs";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { ChartPoint } from "../ChartPoint/ChartPoint";

import { TextSerif } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { getAuctionResultBadgeColors } from "@/utils/auctionResultBadge";
import { formatCurrency } from "@/utils/formatEuro";
import {
  getChartPointColor,
  getChartPointShape,
  getPriceOnApplicationLabel,
  getTransactionNotAvailableLabel,
  isPriceOnApplication,
} from "@/utils/transactionRecord";

import type { Currency } from "@/types/currency";
import type { FC } from "react";

export type AuctionsChartTooltipProps = {
  payload?: Record<string, any>[];
  zeroPriceLabel?: "poa" | "not-available";
};

const badgeStyles = {
  root: {
    textTransform: "uppercase" as const,
    whiteSpace: "nowrap" as const,
    maxWidth: "unset",
    overflow: "visible" as const,
    display: "inline-flex" as const,
    alignItems: "center" as const,
    fontWeight: 600,
    letterSpacing: "0.02em",
  },
  label: {
    whiteSpace: "nowrap" as const,
    overflow: "visible" as const,
    textOverflow: "clip" as const,
    maxWidth: "unset",
    display: "inline-flex" as const,
    alignItems: "center" as const,
  },
};

export const AuctionsChartTooltip: FC<AuctionsChartTooltipProps> = ({
  payload,
  zeroPriceLabel = "not-available",
}) => {
  const { t } = useTranslation();

  if (!payload) {
    return null;
  }

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  function renderItem(item: Record<string, any>) {
    switch (item.name) {
      case "value": {
        const record = item.payload ?? {};
        const showPoa = zeroPriceLabel === "poa" && isPriceOnApplication(item.value);
        const showNotAvailable =
          zeroPriceLabel === "not-available" && isPriceOnApplication(item.value);
        const badgeColors = getAuctionResultBadgeColors(record?.result);
        const badgeLabel =
          typeof record?.result === "string" && record.result.trim() !== ""
            ? String(t(`components.TransactionTableRow.result.${record.result}`, record.result))
            : null;
        const pointShape = getChartPointShape(record);
        const pointColor = getChartPointColor(record);
        const carTypeKey = record.replica ? "replica" : record.outlier ? "outlier" : null;

        const priceLabel = showPoa
          ? getPriceOnApplicationLabel(t)
          : showNotAvailable
            ? getTransactionNotAvailableLabel(t)
            : formatCurrency(item.value, currency.code, {
                notation: "standard",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });

        return (
          <Fragment key={item.name}>
            <TextSerif size="xs" style={{ whiteSpace: "nowrap" }}>
              {dayjs.unix(record.date).format("DD-MM-YYYY") || "-"}
            </TextSerif>

            {carTypeKey && (
              <Group gap={6} wrap="nowrap">
                <svg width={14} height={14} aria-hidden>
                  <ChartPoint cx={7} cy={7} fill={pointColor} shape={pointShape} size={5} />
                </svg>
                <Text fw={700} size="sm" style={{ whiteSpace: "nowrap" }} tt="uppercase">
                  {t(`components.AuctionsChart.legend.${carTypeKey}`)}
                </Text>
              </Group>
            )}

            {badgeColors && badgeLabel && (
              <Badge
                radius="xl"
                size="md"
                styles={{
                  root: {
                    ...badgeStyles.root,
                    backgroundColor: badgeColors.backgroundColor,
                    color: badgeColors.color,
                  },
                  label: badgeStyles.label,
                }}
              >
                {badgeLabel}
              </Badge>
            )}
            <Text fw={500} size="sm" style={{ whiteSpace: "nowrap" }}>
              {priceLabel}
            </Text>
          </Fragment>
        );
      }
      default:
        return null;
    }
  }

  return (
    <Paper
      px="md"
      py="sm"
      radius="md"
      withBorder
      shadow="md"
      style={{ width: "max-content", maxWidth: "unset" }}
    >
      <Stack align="flex-start" gap="xs">
        {payload.map((item) => renderItem(item))}
      </Stack>
    </Paper>
  );
};
